import { NIGHT_TINT, TAP_LIMIT } from 'app/constants/AppConstants.js';

export const SIZE_GROUND = 0.95;
export const SIZE_HAND = 0.8;
export const SIZE_PICKED = 1.1;

export const TAP_TIME = 1.2;
export const SHIFT_UP_MOBILE = -35;
export const SHIFT_UP_DESKTOP = 5;

export default class Item extends Phaser.Sprite {
  /* eslint-disable max-statements */
  constructor({ game, location, data }) {
    super(game, 0, 0, 'items', data.id);

    this.location = location; // reference to Location
    this.data = { ...data };

    this.id = this.data.id;
    this.type = this.data.type;
    this.flags = this.data.flags.concat();
    this.spawn = this.data.spawn ? this.data.spawn.concat() : null;
    this.stackType = this.data.stack_type;

    this.lightPower = 1;
    this.updateLights = true;

    this.isItem = true;
    this.inHand = false;
    this.trashed = false;
    this.drag = false;
    this.dz = 0;
    this.tr = 0xfe;
    this.tg = 0xfe;
    this.tb = 0xfe;

    this.emptyBB = { x: 0, y: 0, width: 0, height: 0 };

    this.anchor.set(0.5, 1);
    this.setScale(SIZE_GROUND);

    this.onDrop = new Phaser.Signal();
    this.onChange = new Phaser.Signal();

    if (this.hasFlag('move') || this.spawn) {
      this.inputEnabled = true;
      this.input.useHandCursor = true;
      this.events.onInputDown.add(this.handleInputDown, this);

      // hover
      if (this.game.deviceMetric.platform.type === 'desktop') {
        // desktop
        this.events.onInputOver.add(this.handleInputOver, this);
        this.events.onInputOut.add(this.handleInputOut, this);
      } else {
        // mobile
        this.events.onInputUp.add(this.handleInputUp, this);
      }
    }

    //
    const minSize = this.game.deviceMetric.platform.type === 'desktop' ? 45 : 70;
    if (this.width < minSize || this.height < minSize) {
      this.createBiggerHitArea(minSize);
    }
  }
  /* eslint-enable */

  init(itemData) {
    if (itemData && itemData.in_hand) {
      this.isFalling = false;
      const character = this.location.characters.find((c) => c.id === itemData.in_hand.character);
      character.putItemIntoHand(
        this,
        itemData.in_hand.hand === 'left' ? character.slot1 : character.slot2
      );
    }

    if (itemData) {
      this.inPond = itemData.in_pond;
    }
  }

  setScale(scale) {
    this.scale.set(scale);
  }

  createBiggerHitArea(minSize) {
    this.area = this.game.make.image(0, 0, 'empty');
    this.area.alpha = 0.2;
    this.area.width = Math.max(minSize, this.width);
    this.area.height = Math.max(minSize, this.height);
    this.area.anchor.set(0.5, 1);
    this.area.y = (this.area.height - this.height) * 0.5;
    this.addChild(this.area);
  }

  get depth() {
    return this.y + this.dz + (this.drag ? 2000 : 0);
  }

  hasFlag(flag) {
    return this.flags.indexOf(flag) >= 0;
  }

  hasType(type) {
    return this.type && this.type.indexOf(type) >= 0;
  }

  updateBounds() {
    this.bb = this.visible ? this.getBounds() : this.emptyBB;
  }

  getPosForAnts() {
    return this.position;
  }

  //
  canBeTrashed() {
    return !this.trashed && this.hasFlag('trashable');
  }

  isRecyclable() {
    if (this.type) {
      for (let i = 0; i < this.type.length; i++) {
        const type = this.type[i];
        if (type === 'rubbish_paper' || type === 'rubbish_plastic') {
          return true;
        }
      }
    }

    return false;
  }

  canBeHold() {
    return this.hasFlag('hold');
  }

  canBeEaten() {
    return this.hasFlag('eat');
  }

  canBeComposted() {
    return !this.trashed && this.hasFlag('compostable');
  }

  canFloat() {
    return this.hasFlag('float');
  }

  canBeStored() {
    return this.hasFlag('storable');
  }

  isSleepingBag() {
    return this.hasFlag('sleeping_bag');
  }

  isHat() {
    return this.hasFlag('hat');
  }

  isFlammable() {
    return this.hasFlag('flammable');
  }

  isTargetForAnt() {
    return this.hasFlag('ant') && !this.drag && !this.inHand && !this.isFalling;
  }

  //
  getTop(bb) {
    if (this.data.top) {
      const bb1 = new Phaser.Rectangle();
      const len = this.data.top.length;
      for (let i = 0; i < len; i++) {
        const top = this.data.top[i];
        bb1.x = this.bb.x + top.x;
        bb1.y = this.bb.y + top.y;
        bb1.width = top.w;
        bb1.height = top.h;
        if (Phaser.Rectangle.intersects(bb, bb1)) {
          return { y: bb1.y, h: top.h };
        }
      }
    }
    return null;
  }

  getSide(bb) {
    if (this.data.side) {
      const bb1 = new Phaser.Rectangle();
      const len = this.data.side.length;
      for (let i = 0; i < len; i++) {
        const side = this.data.side[i];
        bb1.x = this.bb.x + side.x;
        bb1.y = this.bb.y + side.y;
        bb1.width = side.w;
        bb1.height = side.h;
        if (Phaser.Rectangle.intersects(bb, bb1)) {
          return { y: bb1.y, h: side.h };
        }
      }
    }
    return null;
  }

  //
  setLight(power) {
    // console.log('set light', power);
    const r0 = 0xff;
    const g0 = 0xff;
    const b0 = 0xff;

    const tint = NIGHT_TINT;
    const r1 = tint >> 16;
    const g1 = (tint >> 8) & 0x00ff;
    const b1 = tint & 0x0000ff;

    this.tr = r1 + power * (r0 - r1);
    this.tg = g1 + power * (g0 - g1);
    this.tb = b1 + power * (b0 - b1);

    this.updateTint();
  }

  updateTint(tween, value, tweenData) {
    tween;
    tweenData;
    value;
    const r = this.tr | 0;
    const g = this.tg | 0;
    const b = this.tb | 0;
    const tint = (r << 16) | (g << 8) | b;

    this.tint = tint;
  }

  //
  enable() {
    this.input.enabled = true;
  }

  disable() {
    this.input.enabled = false;
  }

  canHover() {
    return this.hasFlag('move') && !this.drag && this.spawn && !this.isBackpack;
  }

  handleInputOver() {
    if (this.canHover()) {
      this.isHover = true;
      this.hoverTime = 0;
      this.location.state.gameHud.showHoverGauge(
        this.worldPosition.x,
        this.worldPosition.y - this.height * 0.5
      );
    }
  }

  handleInputOut() {
    if (this.isHover) {
      this.isHover = false;
      this.location.state.gameHud.hideHoverGauge();
    }
  }

  handleInputDown(target, pointer) {
    target;
    this.handleInputOut();

    if (this.drag) {
      this.handleInputUp();
    } else {
      this.startPos = { x: pointer.x, y: pointer.y };
      this.pointer = pointer;
      if (this.hasFlag('move')) {
        this.location.state.interacted(this);
        this.location.startDragItem(this);
        this.drag = true;

        this.dz = 0;
        this.parentObj && this.parentObj.removeObject(this);
        this.parentObj = null;

        this.dragDx = this.x - (pointer.x - this.location.x);
        this.dragDy = this.y - pointer.y;

        this.parent.bringToTop(this);

        this.setScale(SIZE_PICKED);

        this.handleStartDrag();
      }
    }
  }

  handleStartDrag() {}

  handleInputUp() {
    this.location.stopDragItem(this);
    this.drag = false;
    this.updateBounds();

    this.setScale(SIZE_GROUND);

    // check if it was moved
    const dx = this.pointer.x - this.startPos.x;
    const dy = this.pointer.y - this.startPos.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    this.pointer = null;

    if (dist < TAP_LIMIT && this.spawn && this.game.deviceMetric.platform.type !== 'desktop') {
      this.handleTap();
    } else {
      // drop item
      this.onDrop.dispatch(this);
    }
  }

  handleTap() {
    this.location.state.interacted(this);

    // fix position of movable items
    if (this.hasFlag('move')) {
      const dy =
        this.game.deviceMetric.platform.type === 'desktop' ? SHIFT_UP_DESKTOP : SHIFT_UP_MOBILE;
      this.y -= dy;
    }

    //
    if (this.hasFlag('spawn_single')) {
      // spawn items one by one
      this.animateStretch();
      this.game.time.events.add(150, this.spawnItem, this, this.x, this.y);
      // drop item
      this.onDrop.dispatch(this);
      //
      this.game.sounds.stopAudioSprite('generic_down', { spriteID: 'sfx' });
    } else {
      this.events.onInputDown.remove(this.handleInputDown, this);
      this.events.onInputUp.remove(this.handleInputUp, this);
      this.animateShrink();
      this.game.time.events.add(150, this.spawnItems, this);
      //
      this.game.sounds.stopAudioSprite('generic_down', { spriteID: 'sfx' });
    }
  }

  spawnItems() {
    this.location.spawnItems(this, this.spawn);

    // play audio on spawn
    switch (this.id) {
      case 'banana':
      case 'banana_bunch':
        this.game.sounds.playSFX('item_banana', { spriteID: 'sfx' });
        break;
      case 'pickle_jar':
      case 'peanut_butter':
        this.game.sounds.playSFX('item_jar', { spriteID: 'sfx' });
        break;
      case 'watermelon':
      case 'bread_loaf':
        this.game.sounds.playSFX('item_melon', { spriteID: 'sfx' });
        break;
      case 'marshmallow_bag':
      case 'cracker_box':
      case 'chocolate_bar':
        this.game.sounds.playSFX('item_foodopen', { spriteID: 'sfx' });
        break;
      case 'stick_bundle':
        this.game.sounds.playSFX('item_sticks', { spriteID: 'sfx' });
        break;
      case 'sleeping_bag_1':
      case 'sleeping_bag_1_open':
      case 'sleeping_bag_2':
      case 'sleeping_bag_2_open':
      case 'sleeping_bag_3':
      case 'sleeping_bag_3_open':
        this.game.sounds.playSFX('item_sleepingbag', { spriteID: 'sfx' });
        break;
    }

    //
    this.onChange.dispatch();
  }

  spawnItem(x, y) {
    this.location.spawnItem(this, this.items.shift(), x, y);
    if (this.items.length === 0) {
      // if no more items do something with it
      this.onEmpty();
    }

    // play audio on spawn
    switch (this.id) {
      case 'backpack_ari':
      case 'backpack_elinor':
      case 'backpack_olive':
        this.game.sounds.playSFX('item_combine', { spriteID: 'sfx' });
        break;
    }

    //
    this.onChange.dispatch();
  }

  drop() {
    this.location.startDragItem(this);
    this.location.stopDragItem(this);
    this.onDrop.dispatch(this);
  }

  onEmpty() {
    this.spawn = null;
    if (this.hasFlag('destroy')) {
      this.location.removeItem(this);
    } else if (this.hasFlag('make_movable')) {
      this.flags.push('move');
    }
  }

  //
  putInHand(character, left) {
    this.inHand = true;
    this.character = character;
    let scaleX = 1;
    if (this.data.in_hand) {
      this.anchor.set(this.data.in_hand.anchor.x, this.data.in_hand.anchor.y);
      this.angle = left ? this.data.in_hand.angle_left : this.data.in_hand.angle_right;
      if (left && this.data.in_hand.scale_left) {
        scaleX = this.data.in_hand.scale_left;
      }
    } else {
      this.anchor.set(0.5, 0.75);
      this.angle = left ? -10 : 10;
    }
    this.x = 0;
    this.y = 0;

    this.setScale(SIZE_HAND);
    this.scale.x *= scaleX;
  }

  pickupFromHand() {
    this.anchor.set(0.5, 1);
    this.angle = 0;
    this.inHand = false;
    this.character.pickupItemFromHand();
    this.character = null;
  }

  pickupFromPond() {
    this.inPond = false;
    this.pond = null;
    this.dz = 0;
  }

  //
  fallDownTo(destY) {
    this.destY = destY;
    this.speedY = 0;
    this.isFalling = true;
  }

  throwOutTo(x, y) {
    this.x = x;
    this.y = y;
    this.game.add
      .tween(this)
      .to(
        { x: x + Phaser.Math.between(-20, 20), y: y - 30 },
        150,
        Phaser.Easing.Quadratic.Out,
        true
      )
      .onComplete.addOnce(this.handleThrowEnd, this);
  }

  handleThrowEnd() {
    this.location.setDestinationPosition(this);
  }

  startWaving() {
    this.isWaving = true;
    this.pondTime = 0;
  }

  canStack(stackType) {
    return this.stackType === stackType && !this.stack && !this.hasType('stack') && !this.container;
  }

  hitGround() {}

  /* eslint-disable max-statements */
  step(dt) {
    // check visibility
    if (!this.inPond && !this.inHand && !this.trashed && !this.container && !this.stack) {
      const left = -this.location.x;
      const right = left + this.game.width;
      if (this.x - this.width * 0.5 > right || this.x + this.width * 0.5 < left) {
        this.visible = false;
      } else {
        this.visible = true;
      }
    }

    // hover
    if (this.game.deviceMetric.platform.type === 'desktop') {
      if (this.isHover) {
        this.hoverTime += dt;
        const value = Math.min(1, this.hoverTime / TAP_TIME);
        this.location.state.gameHud.setHoverValue(value);
        if (this.hoverTime > TAP_TIME) {
          this.handleInputOut();
          this.handleTap();
        }
      }
    }

    //
    if (this.drag && this.pointer) {
      // console.log(this.pointer.x, this.pointer.y);
      this.x = -this.location.x + this.pointer.x; // + this.dragDx;
      this.x = Phaser.Math.clamp(this.x, 50, this.location.width - 50);
      const dy =
        this.game.deviceMetric.platform.type === 'desktop' ? SHIFT_UP_DESKTOP : SHIFT_UP_MOBILE;
      this.y = this.pointer.y + dy; // + this.dragDy;
      this.updateLights = true;
    } else if (this.isFalling) {
      this.speedY += 50 * dt;
      this.y += this.speedY;
      // console.log(this.y + this.dz, this.y, this.dz);
      if (this.y > this.destY) {
        // reached destination position
        this.y = this.destY;
        this.isFalling = false;
        if (!this.parentObj) {
          this.dz = 0;
        } else {
          this.dz = this.destDZ;
          this.destDZ = 0;
        }

        // splash sound if in water
        if (this.inPond) {
          this.game.sounds.playSFX('item_splash', { spriteID: 'sfx' });
        } else {
          this.hitGround();
        }

        //
        this.onChange.dispatch();
      }
    } else if (this.inPond && this.isWaving) {
      this.pondTime += dt;
      // const cos = Math.cos(this.pondTime * 1.5);
      const sin = Math.sin(this.pondTime);
      const sin2 = Math.sin(this.pondTime * 2);
      this.y = this.destY + 2 + 2 * sin;
      this.angle = 2 * sin2;
    }

    // check light
    let power;
    const lightsChanged = this.location.lights.changed;
    if (this.updateLights) {
      // force update lights
      this.location.lights.changed = true;
    }
    if (this.visible) {
      power = this.location.lights.getLightAt(
        this.worldPosition.x,
        this.worldPosition.y - (this.height >> 1)
      );
    } else {
      power = this.location.lights.getLightAt(
        this.worldPosition.x,
        this.worldPosition.y - (this.height >> 1),
        true
      );
    }
    if (power >= 0 && power !== this.lightPower) {
      this.lightPower = power;
      this.setLight(power);
    }
    if (this.updateLights) {
      this.updateLights = false;
      this.location.lights.changed = lightsChanged;
    }
  }
  /* eslint-enable */

  //
  animateToPosition(x, y, time = 500, inPond = false) {
    if (inPond) {
      this.game.add.tween(this).to({ x: x }, time, Phaser.Easing.Quadratic.Out, true);
    } else {
      this.game.add
        .tween(this)
        .to({ x: x, y: y }, time, Phaser.Easing.Quadratic.Out, true)
        .onComplete.add(this.handleOnPosition, this);
    }
  }

  handleOnPosition() {
    this.onChange.dispatch();

    if (!this.location.setDestinationPosition(this)) {
      if (this.parentObj) {
        this.parentObj.removeObject(this);
        this.parentObj = null;
      }
    }
  }

  animateOnCraft() {
    this.game.add
      .tween(this.scale)
      .to({ x: [1.25, SIZE_GROUND], y: [1.25, SIZE_GROUND] }, 350, null, true);
  }

  animateShrink() {
    this.game.add
      .tween(this.scale)
      .to({ x: 0, y: 0 }, 150, null, true)
      .onComplete.add(this.handleShrinkComplete, this);
  }

  animateSquize() {
    this.game.add.tween(this.scale).to({ y: [1.1, 0.9, 1] }, 500, null, true);
  }

  animateStretch() {
    this.game.add.tween(this.scale).to({ y: [0.9, 1.1, 1] }, 500, null, true);
  }

  animateGrowUp() {
    this.game.add.tween(this.scale).to({ x: SIZE_GROUND, y: SIZE_GROUND }, 150, null, true);
  }

  handleShrinkComplete() {
    this.location.removeItem(this);
  }

  pulse(time = 1500) {
    const s0 = 0.8;
    const s1 = 1.3;
    const tween1 = this.game.add
      .tween(this.scale)
      .to({ x: [s1, s0, s1, s0, s1, s0, 1], y: [s0, s1, s0, s1, s0, s1, 1] }, time, null, false);
    const tween2 = this.game.add
      .tween(this.scale)
      .to(
        { x: [s1, s0, s1, s0, s1, s0, 1], y: [s0, s1, s0, s1, s0, s1, 1] },
        time,
        null,
        false,
        200
      );
    tween1.chain(tween2);
    tween1.start();
  }

  destroy() {
    this.events.onInputDown.remove(this.handleInputDown, this);
    this.events.onInputUp.remove(this.handleInputUp, this);

    super.destroy();
  }

  serialize() {
    // do not serialize items in containers and stacks
    if (this.container || this.stack) {
      return null;
    }

    const data = { id: this.id, x: this.x, y: this.y };
    //
    if (this.inHand) {
      data.in_hand = {};
      data.in_hand.character = this.character.id;
      data.in_hand.hand = this.parent === this.character.slot1 ? 'left' : 'right';
    }

    data.in_pond = this.inPond;

    return data;
  }
}
